import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';

const PublishingControls = ({ caseData, onPublish, onSaveDraft }) => {
  const [publishStep, setPublishStep] = useState('draft');
  const [verdictData, setVerdictData] = useState({
    verdict: '',
    confidence: 75,
    reasoning: '',
    limitations: '',
    sources: [],
    tags: []
  });
  const [approvalStatus, setApprovalStatus] = useState({
    primaryReviewer: null,
    secondaryReviewer: null,
    efcsnApproval: false
  });

  // Mock approval data
  const mockApprovals = {
    primaryReviewer: {
      name: 'Dr. Elena Rossi',
      approvedAt: new Date(Date.now() - 3600000),
      status: 'approved',
      comments: 'Comprehensive analysis with strong evidence base. Ready for publication.'
    },
    secondaryReviewer: {
      name: 'Marcus Weber',
      approvedAt: null,
      status: 'pending',
      comments: null
    }
  };

  // Verdict options
  const verdictOptions = [
    { value: 'true', label: 'True', description: 'Claim is accurate and supported by evidence' },
    { value: 'mostly_true', label: 'Mostly True', description: 'Claim is largely accurate with minor inaccuracies' },
    { value: 'half_true', label: 'Half True', description: 'Claim contains both accurate and inaccurate elements' },
    { value: 'mostly_false', label: 'Mostly False', description: 'Claim is largely inaccurate with some accurate elements' },
    { value: 'false', label: 'False', description: 'Claim is inaccurate and not supported by evidence' },
    { value: 'unverifiable', label: 'Unverifiable', description: 'Insufficient evidence to determine accuracy' }
  ];

  // Distribution platforms
  const distributionPlatforms = [
    { id: 'facebook', name: 'Facebook', enabled: true, status: 'ready' },
    { id: 'twitter', name: 'Twitter/X', enabled: true, status: 'ready' },
    { id: 'youtube', name: 'YouTube', enabled: false, status: 'not_applicable' },
    { id: 'tiktok', name: 'TikTok', enabled: true, status: 'ready' },
    { id: 'instagram', name: 'Instagram', enabled: true, status: 'ready' },
    { id: 'linkedin', name: 'LinkedIn', enabled: false, status: 'not_applicable' }
  ];

  const handleVerdictChange = (field, value) => {
    setVerdictData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePublish = () => {
    if (canPublish()) {
      onPublish?.({
        ...verdictData,
        approvals: mockApprovals,
        publishedAt: new Date(),
        platforms: distributionPlatforms?.filter(p => p?.enabled)
      });
    }
  };

  const canPublish = () => {
    return (verdictData?.verdict &&
    verdictData?.reasoning?.length > 50 &&
    mockApprovals?.primaryReviewer?.status === 'approved' && mockApprovals?.secondaryReviewer?.status === 'approved');
  };

  const getVerdictColor = (verdict) => {
    switch (verdict) {
      case 'true': return 'text-success';
      case 'mostly_true': return 'text-success';
      case 'half_true': return 'text-warning';
      case 'mostly_false': return 'text-destructive';
      case 'false': return 'text-destructive';
      case 'unverifiable': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Send" size={20} className="text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">Publishing Controls</h3>
            <p className="text-sm text-muted-foreground">
              Two-person rule • EFCSN standards compliance
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <div className={`
            px-3 py-1 rounded-full text-sm font-medium
            ${canPublish() ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}
          `}>
            {canPublish() ? 'Ready to Publish' : 'Pending Approvals'}
          </div>
        </div>
      </div>
      {/* Publishing Steps */}
      <div className="flex-1 overflow-y-auto">
        {/* Step 1: Verdict Formulation */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center space-x-2 mb-4">
            <div className={`
              w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium
              ${verdictData?.verdict ? 'bg-success text-success-foreground' : 'bg-muted text-muted-foreground'}
            `}>
              {verdictData?.verdict ? <Icon name="Check" size={14} /> : '1'}
            </div>
            <h4 className="font-medium text-foreground">Verdict Formulation</h4>
          </div>

          <div className="space-y-4 ml-8">
            <div>
              <Select
                label="Verdict Classification"
                options={verdictOptions}
                value={verdictData?.verdict}
                onChange={(value) => handleVerdictChange('verdict', value)}
                placeholder="Select verdict..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Confidence Level: {verdictData?.confidence}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={verdictData?.confidence}
                onChange={(e) => handleVerdictChange('confidence', parseInt(e?.target?.value))}
                className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>Low</span>
                <span>Medium</span>
                <span>High</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Reasoning & Evidence Summary
              </label>
              <textarea
                value={verdictData?.reasoning}
                onChange={(e) => handleVerdictChange('reasoning', e?.target?.value)}
                placeholder="Provide clear reasoning for the verdict based on evidence analysis..."
                className="w-full h-24 px-3 py-2 border border-border rounded-lg bg-input text-foreground text-sm resize-none"
              />
              <div className="text-xs text-muted-foreground mt-1">
                {verdictData?.reasoning?.length}/500 characters (minimum 50 required)
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Limitations & Uncertainties
              </label>
              <textarea
                value={verdictData?.limitations}
                onChange={(e) => handleVerdictChange('limitations', e?.target?.value)}
                placeholder="Acknowledge any limitations in the analysis or areas of uncertainty..."
                className="w-full h-20 px-3 py-2 border border-border rounded-lg bg-input text-foreground text-sm resize-none"
              />
            </div>
          </div>
        </div>

        {/* Step 2: Peer Review Approvals */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center space-x-2 mb-4">
            <div className={`
              w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium
              ${mockApprovals?.primaryReviewer?.status === 'approved' && mockApprovals?.secondaryReviewer?.status === 'approved' ?'bg-success text-success-foreground' : 'bg-warning text-warning-foreground'}
            `}>
              {mockApprovals?.primaryReviewer?.status === 'approved' && mockApprovals?.secondaryReviewer?.status === 'approved'
                ? <Icon name="Check" size={14} /> : '2'}
            </div>
            <h4 className="font-medium text-foreground">Two-Person Rule Verification</h4>
          </div>

          <div className="space-y-3 ml-8">
            {/* Primary Reviewer */}
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-sm font-medium text-primary-foreground">
                  {mockApprovals?.primaryReviewer?.name?.charAt(0)}
                </div>
                <div>
                  <div className="font-medium text-foreground">Primary Reviewer</div>
                  <div className="text-sm text-muted-foreground">{mockApprovals?.primaryReviewer?.name}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${mockApprovals?.primaryReviewer?.status === 'approved' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}
                `}>
                  {mockApprovals?.primaryReviewer?.status}
                </div>
                {mockApprovals?.primaryReviewer?.status === 'approved' && (
                  <Icon name="CheckCircle" size={16} className="text-success" />
                )}
              </div>
            </div>

            {/* Secondary Reviewer */}
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-sm font-medium text-secondary-foreground">
                  {mockApprovals?.secondaryReviewer?.name?.charAt(0)}
                </div>
                <div>
                  <div className="font-medium text-foreground">Secondary Reviewer</div>
                  <div className="text-sm text-muted-foreground">{mockApprovals?.secondaryReviewer?.name}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
                  pending
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Send"
                  onClick={() => {
                    // Send reminder
                  }}
                >
                  Remind
                </Button>
              </div>
            </div>

            {/* EFCSN Compliance */}
            <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center space-x-3">
                <Icon name="Shield" size={20} className="text-primary" />
                <div>
                  <div className="font-medium text-foreground">EFCSN Standards Compliance</div>
                  <div className="text-sm text-muted-foreground">European Code of Standards verified</div>
                </div>
              </div>
              
              <Icon name="CheckCircle" size={16} className="text-success" />
            </div>
          </div>
        </div>

        {/* Step 3: Distribution Settings */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium bg-muted text-muted-foreground">
              3
            </div>
            <h4 className="font-medium text-foreground">Platform Distribution</h4>
          </div>

          <div className="space-y-3 ml-8">
            <p className="text-sm text-muted-foreground mb-3">
              Select platforms for machine-readable verdict distribution
            </p>
            
            {distributionPlatforms?.map((platform) => (
              <div key={platform?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={platform?.enabled}
                    onChange={() => {
                      // Toggle platform
                    }}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <div>
                    <div className="font-medium text-foreground">{platform?.name}</div>
                    <div className="text-sm text-muted-foreground">
                      Status: {platform?.status?.replace('_', ' ')}
                    </div>
                  </div>
                </div>
                
                <div className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${platform?.status === 'ready' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}
                `}>
                  {platform?.status?.replace('_', ' ')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Verdict Preview */}
        {verdictData?.verdict && (
          <div className="p-4">
            <h4 className="font-medium text-foreground mb-3">Verdict Preview</h4>
            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-center space-x-2 mb-3">
                <div className={`
                  px-3 py-1 rounded-full text-sm font-medium
                  ${verdictData?.verdict === 'true' || verdictData?.verdict === 'mostly_true' ? 'bg-success/10 text-success' :
                    verdictData?.verdict === 'false'|| verdictData?.verdict === 'mostly_false' ? 'bg-destructive/10 text-destructive' : 'bg-warning/10 text-warning'}
                `}>
                  {verdictOptions?.find(v => v?.value === verdictData?.verdict)?.label}
                </div>
                <div className="text-sm text-muted-foreground">
                  Confidence: {verdictData?.confidence}%
                </div>
              </div>
              
              <p className="text-sm text-foreground mb-2">{verdictData?.reasoning}</p>
              
              {verdictData?.limitations && (
                <div className="text-sm text-muted-foreground">
                  <strong>Limitations:</strong> {verdictData?.limitations}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Footer Actions */}
      <div className="flex items-center justify-between p-4 border-t border-border bg-muted/20">
        <div className="flex space-x-2">
          <Button
            variant="outline"
            iconName="Save"
            onClick={() => onSaveDraft?.(verdictData)}
          >
            Save Draft
          </Button>
          <Button
            variant="outline"
            iconName="Eye"
            onClick={() => {
              // Preview verdict
            }}
          >
            Preview
          </Button>
        </div>

        <div className="flex space-x-2">
          <Button
            variant="outline"
            iconName="Clock"
            onClick={() => {
              // Schedule publication
            }}
          >
            Schedule
          </Button>
          <Button
            variant="default"
            iconName="Send"
            onClick={handlePublish}
            disabled={!canPublish()}
          >
            Publish Verdict
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PublishingControls;